import { usePrice, formatPrice } from '@corratech/pylot-price'
import type { PriceRange } from '@pylot-data/pylotschema'
import cn from 'classnames'
import type { ReactElement } from 'react'
import s from '@components/common/ProductPrice/ProductPrice.module.scss'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React from 'react'

export type ProductPrice = {
    priceRange: PriceRange
    className?: string
    productType?: string
    bundlePrice?: { price: number; discountPrice: number } | null
    canUpdateBundlePrice?: boolean
    showDiscountPrice?: boolean
    priceText?: boolean
    is_bulk_product?: boolean
}

export const ProductPrice = ({
    priceRange,
    className = '',
    productType,
    bundlePrice,
    canUpdateBundlePrice,
    showDiscountPrice = true,
    priceText,
    is_bulk_product
}: ProductPrice): ReactElement => {
    const { locale = 'en' } = useRouter()
    const { t } = useTranslation('common')
    const { subtotal, total } = usePrice(priceRange)

    const getConfigProductPrices = (price: PriceRange) => {
        const maxPrice = price?.maximum_price?.final_price
        const minPrice = price.minimum_price.final_price
        if (minPrice?.value! < maxPrice?.value!) {
            return t('From {{min}} to {{max}}', {
                min: formatPrice(minPrice, locale),
                max: formatPrice(maxPrice!, locale)
            })
        }
        return null
    }

    if (
        productType === 'ConfigurableProduct' ||
        productType === 'BundleProduct' ||
        productType === 'GiftCardProduct'
    ) {
        if (bundlePrice?.price && canUpdateBundlePrice) {
            return (
                <div
                    className={cn(
                        s['product-price'],
                        'product-price',
                        className
                    )}
                >
                    <span>
                        {formatPrice(
                            {
                                value: bundlePrice?.discountPrice,
                                currency:
                                    priceRange.minimum_price.regular_price
                                        .currency
                            },
                            locale
                        )}
                    </span>
                    {priceRange?.minimum_price?.regular_price?.value &&
                        bundlePrice?.price !== bundlePrice?.discountPrice &&
                        showDiscountPrice && (
                            <span className="line-through text-gs-2 pl-2 pr-3">
                                {formatPrice(
                                    {
                                        value: bundlePrice?.price,
                                        currency:
                                            priceRange.minimum_price
                                                .regular_price.currency
                                    },
                                    locale
                                )}
                            </span>
                        )}
                </div>
            )
        }
        const configProductPrices = getConfigProductPrices(priceRange)
        if (configProductPrices) {
            return (
                <div
                    className={cn(
                        s['product-price'],
                        'product-price',
                        className
                    )}
                >
                    <span>{configProductPrices}</span>
                </div>
            )
        }
    }

    return (
        <div className={cn(s['product-price'], 'product-price', className)}>
            {priceText && <span>{t(`Price: `)}</span>}
            {Boolean(
                priceRange?.minimum_price?.regular_price?.value &&
                    subtotal !== total &&
                    showDiscountPrice
            ) && (
                <span className="line-through text-gs-2 pl-2 pr-3">
                    {subtotal}
                </span>
            )}
            <span>{total}</span>
        </div>
    )
}
